import { Fragment, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import InputAdornment from '@mui/material/InputAdornment'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useBilling from '../models/useBilling'
import DialogConfirm from './DialogConfirm'
import { useOrgRole } from '../hooks/useOrgRole'
import { formatCents } from '../utilities/general'
import { besideField, besideFieldRow } from '../utilities/formAlign'
import { instantUTC, resetLabel } from '../utilities/freshness'
import { chargeTargetName } from '../utilities/paymentMethod'
import { describeBillingError } from '../utilities/billingError'
import { currentPeriod, formatPeriod } from '../models/useUsage'
import {
  MAX_PURCHASE_ROWS,
  MIN_AUTO_REFILL_CENTS,
  armRefusal,
  capExhaustedClause,
  autoReloadCanFire,
  autoReloadOn,
  autoReloadRefusal,
  refillFieldMessage,
  refillFloorCents,
  dollarsToCents,
  creditByKind,
  grantExpiryLabel,
  needsPurchaseConfirmation,
  purchaseEntryRefusal,
  purchaseStatusLabel,
  refillGaugePercent,
  refillHeadroomCents,
  refillOutlook,
  refusedRefill,
  showsRefillGauge,
  storedRefillTruncation,
  showsCredit,
  showsCreditReadOnly,
  spendCapRefusal,
  pendingAge,
  pendingPurchaseCents,
  capPermitsRefill,
  rulePermitsRefill,
} from '../utilities/credit'
import { saveRefillSettings } from '../utilities/refillSave'

/**
 * The usage-credit block (app#170 items 3-4, app#1083's app half).
 *
 * It carries what Claude's panel carries and ours did not: the balance, what the period has
 * SPENT with its reset date, the monthly maximum, the auto-reload rule, and the switch that says
 * whether the balance is spent at all.
 *
 * ⚠⚠ IT RENDERS ON `creditBilled`, NEVER ON `creditEnabled` — see `showsCredit`, which carries
 * the argument. On prod today the deployment bills arrears and this block renders NOTHING, which
 * is correct: there is no balance to spend and `op=buy` is refused `failed_precondition`. It
 * lights up when `NOBGP_CREDIT_BILLING_ENABLED` goes true.
 *
 * ⚠ FREE SEES NONE OF IT — no balance, no switch, no maximum. `docs/PRICING.md`: "The free plan
 * has no credit and never sees this choice." ⚠ EXCEPT READ-ONLY, ON THE BILLING PAGE (app#265): a
 * Free organization that came down from a paid plan still holds the credit it bought, and the
 * billing page passes `readOnly` so that balance and its purchase list are shown with no control
 * — no Buy, no Auto-refill, no spending switch. Free is hard-capped and spends no credit, so a
 * purchase there buys something the organization cannot use until it subscribes again. The usage
 * page passes `isFree` alone and still shows Free nothing: its bars already say what happens at the
 * allowance, and a balance beside them would read as money that could stop it.
 *
 * ⚠⚠ THE MONTHLY MAXIMUM IS EDITED HERE NOW, AND IT USED TO BE ON THE BILLING PAGE (app#190).
 * It moved because nobgp-router#1195 narrowed what it means: it caps AUTOMATIC refills and no
 * longer touches a manual purchase, so it is one of the three numbers of ONE rule — below this,
 * buy that, never more than this a month — and the three are now saved together by one button.
 * A section of its own, on another panel, described a ceiling on spending in general, which is
 * what it is not. There is still exactly ONE control for it; the Billing page no longer has one.
 *
 * ⚠ NO "UNLIMITED" MAXIMUM IS OFFERED, deliberately (app#170) — `set_spend_cap` refuses the idea
 * in as many words; unlimited overage is an Enterprise conversation, not a control.
 */
export default function CreditBlock({ isFree, readOnly = false }: Readonly<{ isFree: boolean; readOnly?: boolean }>) {
  const credit = useBilling(s => s.credit)
  const creditGrants = useBilling(s => s.creditGrants)
  // ⚠ One selector, one field — a `state => ({…})` here is the Zustand v5 snapshot loop.
  const creditPurchases = useBilling(s => s.creditPurchases)
  const buyCredit = useBilling(s => s.buyCredit)
  const setAutoReload = useBilling(s => s.setAutoReload)
  const setCreditSpending = useBilling(s => s.setCreditSpending)
  // ⚠ The write halves and the reads, so this panel's Save can put both writes out before either
  // read (app#293). This was the store's only caller of `setSpendCap` — the composed write-then-read
  // — so that action is gone with it; `setAutoReload` survives for `onToggleRefill` below, which
  // makes ONE change and wants the read that follows it.
  const writeSpendCap = useBilling(s => s.writeSpendCap)
  const writeAutoReload = useBilling(s => s.writeAutoReload)
  const rereadRefillState = useBilling(s => s.rereadRefillState)
  const rereadAfterRefillWrite = useBilling(s => s.rereadAfterRefillWrite)
  // ⚠ NAMED IN THE PURCHASE CONFIRMATION, AND NULL IS AN ORDINARY ANSWER (app#206). `loadSummary`
  // is what fills it, and both pages that host this block call it now (app#268). It is still null
  // for an org with no method, before the read lands, and for a Member — then the dialog and the
  // caption say "your default payment method", which names nothing rather than naming an
  // instrument the organization may not have (app#241).
  // ⚠ One selector, one field: a `state => ({…})` here is the Zustand v5 snapshot loop.
  const card = useBilling(s => s.card)
  const { can } = useOrgRole()
  const canManage = can('billing.manage') // Owner only — the router re-checks (ActionManageBilling)

  const [amount, setAmount] = useState('')
  const [trigger, setTrigger] = useState('')
  const [reload, setReload] = useState('')
  // ⚠ The cap moved here from the billing page (nobgp-router#1195): it bounds automatic refills
  // and nothing else, so it belongs beside the rule it bounds. Seeded from the stored value and
  // re-seeded when that changes, exactly as the old SpendCap block did.
  const [cap, setCap] = useState('')
  const [busy, setBusy] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  // ⚠⚠ THE SWITCH SLIDES ON EVERY PRESS, AND THIS FLAG IS WHAT LETS IT (app#197). `checked` reads
  // this OR the stored rule. Benoit, 2026-09-03: the switch "does just enable the fields (and
  // slides)". Three PRs each removed one reason the press could be refused and left the next one,
  // because the switch was bound to the STORED rule. A press that stored nothing snapped it back.
  //
  // ⚠⚠ ON DOES NOT MEAN STORED. This flag is set when the customer slides the switch On and no
  // rule is saved yet. Nothing is charged while it is set. No rule is stored, so no card is
  // touched. The caption says this in words, in warning colour.
  //
  // ⚠ IT SURVIVES AN EDIT TO THE FIELDS, and the flag it replaces did not. The customer slides
  // the switch On and then types the numbers it asked for. The switch must stay On while they
  // type.
  //
  // ⚠⚠ IT IS CLEARED ON A CHANGE OF ORGANIZATION — see the seeding effect. The two fields are
  // blanked for a new organization. A stale flag would open org B's panel with auto-refill On for
  // a press its owner made in org A.
  const [armIntent, setArmIntent] = useState(false)
  // ⚠⚠ WHAT THE OFF PRESS DESTROYED, KEPT FOR THIS SESSION ONLY (app#205). The press writes at once
  // and clears the stored pair — measured on prod, 1000/2000 to NULL on the press alone — while
  // Save stays disabled, so nothing on the panel said a write had happened. The behaviour is
  // app#197's decided design and stays; this is the acknowledgement it was missing, and it is what
  // lets the pair be offered back instead of retyped from memory.
  //
  // ⚠ SET ONLY AFTER THE WRITE LANDS, never beside the local field clear: a failed call leaves the
  // rule stored, and saying it was cleared would be a wrong statement about the customer's money.
  //
  // ⚠⚠ AND CLEARED ON A CHANGE OF ORGANIZATION, for the reason `armIntent` is — otherwise org B is
  // offered org A's rule back, under a sentence saying org B's was cleared.
  const [cleared, setCleared] = useState<{ trigger: number; reload: number } | undefined>()
  const triggerRef = useRef<HTMLInputElement>(null)
  const reloadRef = useRef<HTMLInputElement>(null)
  const capRef = useRef<HTMLInputElement>(null)

  // ⚠ The field the cursor goes to when the switch opens the row. It cannot be focused inside the
  // press handler: the field is still `disabled` there, and `focus()` on a disabled input does
  // nothing. This holds it until React has committed the enabled row.
  const focusOnArm = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (!armIntent) return
    focusOnArm.current?.focus()
    focusOnArm.current = null
  }, [armIntent])

  // ⚠ Seeded from the stored values and re-seeded when they change, so the row shows the rule in
  // force rather than three empty boxes beside a switch that says On. `useEffect` runs before the
  // early return below only because hooks must — the values are null until the position loads.
  //
  // ⚠⚠ A STORED NULL DOES NOT CLEAR THE TWO FIELDS. Two things clear them: a different
  // organization, and the Off press itself, which clears them directly (app#197). This guard
  // protects the customer who is TYPING. They slide the switch On, they type the trigger, and a
  // refetch of the position must not erase a half-typed number. A stored null means "no rule". It
  // does not mean "empty the boxes".
  //
  // ⚠ The organization guard is why this is not simply "never clear". Without it the fields carry
  // one organization's rule into the panel of another that has none.
  const seededOrg = useRef<string | null>(null)
  useEffect(() => {
    const orgChanged = seededOrg.current !== (credit?.orgId ?? null)
    seededOrg.current = credit?.orgId ?? null
    // ⚠⚠ AN UNSAVED ARM DOES NOT TRAVEL BETWEEN ORGANIZATIONS. `armIntent` holds the switch On
    // for a rule the customer asked for and has not saved. The two fields are blanked for a new
    // organization two lines down. Without this, org B's panel opens with auto-refill On for a
    // press its owner made in org A.
    if (orgChanged) {
      setArmIntent(false)
      setCleared(undefined)
    }
    const seed = (cents: number | null | undefined, typed: string) =>
      cents != null ? String(cents / 100) : orgChanged ? '' : typed
    setTrigger(typed => seed(credit?.autoReloadTriggerCents, typed))
    setReload(typed => seed(credit?.autoReloadReloadCents, typed))
    setCap(String((credit?.spendCapCents ?? 0) / 100))
  }, [credit?.orgId, credit?.autoReloadTriggerCents, credit?.autoReloadReloadCents, credit?.spendCapCents])

  // ⚠ TWO GATES, ONE PER VIEW (app#265). `readOnly` is the billing page's view of a Free
  // organization that holds credit: shown when there is a balance or a purchase to show, never
  // for an empty one. Without it `showsCredit` decides, and Free is hidden as before.
  if (!(readOnly ? showsCreditReadOnly(credit, creditPurchases) : showsCredit(credit, isFree))) return null
  // The gate has established it; the assertion is for the compiler, which cannot see through it.
  const position = credit!

  // ⚠⚠ THE THROWN TEXT IS NEVER RENDERED (app#200). `e.message` here is a GO error: pressing Buy
  // on prod put `buy credit: reserve credit purchase for org <uuid>: sql: Scan error … converting
  // driver.Value type string` in the red alert below. `describeBillingError` maps the router's
  // cause to a sentence that names a remedy; the original goes to the console.
  //
  // ⚠⚠ ONLY `buy` CHARGES A CARD, and only it may raise the question of a charge. Save stores the
  // auto-refill rule and the period maximum, and the switch stores whether the balance is spent —
  // none of the three moves money, so telling their reader that a card was untouched would attach
  // a money sentence to a press that was never about money. Derived from the id here rather than
  // passed at each call site: `run` is the whole error path, and the four callers are being
  // edited by sibling issues.
  const run = async (id: string, fn: () => Promise<void>) => {
    setError(undefined)
    setBusy(id)
    try {
      await fn()
    } catch (e) {
      console.error('credit action failed', id, e)
      setError(describeBillingError(e, id === 'buy' ? 'charge' : 'manage'))
    } finally {
      setBusy(undefined)
    }
  }

  // ⚠ Dollars in the field, cents on the wire. The parse moved to `utilities/credit.ts` as
  // `dollarsToCents` so that its rounding is TESTED: a typed $1.005 became a $1.01 charge here and
  // nothing could reach the function to prove it (app#206). The short local name stays — four
  // fields read it — and `purchaseEntryRefusal` is what guards the one that charges a card.
  const toCents = dollarsToCents

  const refillOn = autoReloadOn(position)
  // ⚠ What the SWITCH shows, and what the three fields below are enabled by. `refillOn` stays the
  // STORED rule, and every sentence about money is still derived from that one.
  const switchOn = refillOn || armIntent
  const byKind = creditByKind(creditGrants)
  const refillHeadroom = refillHeadroomCents(position)
  // ⚠ THE NAMED RULE, NOT A `> 0` HERE. `autoReloadCanFire` is where "armed but bounded to
  // nothing" is decided, and `BillingBanner` asks the same function about the same state — two
  // surfaces that must not disagree about the customer's money. It already includes
  // `autoReloadOn`, so under `refillOn` it answers the headroom alone.
  const refillCanFire = autoReloadCanFire(position)
  const capCents = position.spendCapCents ?? 0
  const refilled = position.autoRefilledThisPeriodCents ?? 0
  const resetDue = resetLabel(position.period, currentPeriod(), position.periodEnd)
  // ⚠⚠ THE MONTH IS NAMED, NOT CALLED "this period" (app#272). The usage page has a six-month
  // picker, and the rings and tables follow it; this block does not — `loadCredit` takes no period
  // and the router answers about the CURRENT one — so under a page headed August it said "this
  // period" about September's purchases. The read stays current, deliberately: the balance is not
  // per period and purchased credit does not expire, so there is no August balance to show. The
  // headings say which month they mean instead. `formatPeriod` is the picker's own formatter.
  const periodName = formatPeriod(position.period)
  // ⚠ The rule's last attempt, when it was refused — the caption under the switch names it.
  const refused = refusedRefill(creditPurchases)
  const amountCents = toCents(amount)
  // ⚠⚠ IT READS THE TYPED STRING AND NOT THE CENTS (app#206). `amountCents === null ? 'Enter an
  // amount.' : …` said that about a NEGATIVE value and about a sub-cent one, because both parse to
  // the same nothing — a sentence describing an empty box, in front of a customer who had just
  // typed into it. `purchaseEntryRefusal` keeps one message per cause and is where they live.
  const buyRefusal = purchaseEntryRefusal(amount)
  const buyDisabled = !canManage || busy === 'buy' || !!buyRefusal
  // ⚠ The threshold, not every press — see `needsPurchaseConfirmation`.
  const buyNeedsConfirm = amountCents !== null && needsPurchaseConfirmation(amountCents)
  // ⚠ `chargeTargetName`, never `brand` + `last4`: a Stripe Link method has neither (router#1171),
  // and a confirmation that says " •••• " about a real charge is worse than one that names no
  // method at all.
  //
  // ⚠⚠ ONE VALUE, TWO SENTENCES (app#241). The confirmation dialog and the caption under the field
  // are the same fact, and they drifted because each held its own words: the dialog learned to
  // name the method and the caption stayed "the card on file". Both read this now.
  const chargeTarget = chargeTargetName(card)
  // ⚠ THE FIELD, falling back to the residual only while an older router is serving — see
  // `pendingPurchaseCents`.
  const unsettled = pendingPurchaseCents(position)
  // ⚠ The age is field-only: a residual cannot carry one, so it is simply absent on an older
  // router and the sentence below prints without it.
  const unsettledFor = pendingAge(position.oldestPendingPurchaseAt)
  const reloadRefusal = autoReloadRefusal(toCents(trigger), toCents(reload))
  // ⚠⚠ A REFUSAL MARKS THE FIELDS IT IS ABOUT, AND IT USED TO MARK BOTH (app#301). Two of the
  // four refusals name the pair — "set both, or clear both" — and belong on either box; the other
  // two name ONE field, and painting the sibling told a customer their valid $20 was wrong with a
  // sentence about the trigger. `fields` is the refusal's own answer, so this cannot drift from it.
  const triggerError = refillFieldMessage(reloadRefusal, 'trigger', trigger)
  const reloadError = refillFieldMessage(reloadRefusal, 'reload', reload)
  const capRefusal = spendCapRefusal(toCents(cap))
  // ⚠ The cap the customer is LOOKING AT, not the stored one: a cap typed but not yet saved is
  // what they would be sent to change, and the switch saves it below when it arms the rule.
  const arm = armRefusal(toCents(trigger), toCents(reload), toCents(cap) ?? capCents)
  // ⚠⚠ THE DRAFT, WHICH IS THE WHOLE OF app#204. The caption under the switch reads the STORED
  // rule and is right to — but a cap that kills the rule was therefore announced only AFTER it was
  // saved. This asks the same question of the numbers the customer is looking at. It WARNS and
  // does not block Save: parking a rule under a $0 cap is a real need, because the off switch
  // destroys the pair (app#205).
  // ⚠⚠ THE STORED RULE'S OWN TRUNCATION (app#222). `draftOutlook` below answers for the cap being
  // TYPED and goes quiet once it is saved — so the panel warned while the truncation was a
  // prediction and went silent when it became a fact. This is the stored side of the same
  // question, and it is the case that falls through the caption ladder: a rule that still fires,
  // and buys less than it says.
  const storedTruncatedTo = storedRefillTruncation(position)
  const draftOutlook = refillOutlook(
    toCents(reload) ?? position.autoReloadReloadCents,
    toCents(cap) ?? capCents,
    refilled,
  )

  const onBuy = () =>
    run('buy', async () => {
      if (amountCents === null || buyRefusal) return
      await buyCredit(amountCents)
      setAmount('')
    })

  // ⚠⚠ ONE SAVE, TWO ROUTER CALLS, AND IT FIRES ONLY THE ONES WHOSE VALUES CHANGED. The three
  // numbers are one decision — how much am I willing to spend without being asked — so they are
  // edited together; but `set_auto_reload` and `set_spend_cap` are separate mutations, and
  // sending both on every edit would risk a half-failure for a field nobody touched.
  //
  // ⚠ AN EMPTY CAP FIELD IS NOT A ZERO. `toCents('')` is null and `capChanged` refuses it, so
  // clearing the box leaves the stored cap exactly where it was — type 0 to mean zero. The old
  // `SpendCap` block saved an empty field AS 0, and carrying that here would make an accidental
  // clear silently drop an organization's ceiling to nothing.
  const capChanged = toCents(cap) !== null && toCents(cap) !== capCents

  // ⚠⚠ `Save` IS THE ONLY THING THAT STORES THE RULE, AND THE SWITCH IS NOT (app#197). This is
  // the reverse of the gate that stood here. The old gate asked `refillOn`, so Save could only
  // edit a rule that already existed, and only the switch could create one.
  //
  // ⚠⚠ THE DISABLED FIELDS ARE WHAT MAKES THIS SAFE, and they are not decoration. The old gate
  // existed to stop an Owner who typed values to see what a rule would look like, then pressed
  // Save for the cap beside them, and enrolled their card without ever touching the switch. The
  // three fields are dead while the switch is Off now, so there is nothing to type. The press
  // that used to arm the rule opens the fields instead.
  const ruleChanged =
    switchOn &&
    (toCents(trigger) !== position.autoReloadTriggerCents || toCents(reload) !== position.autoReloadReloadCents)

  // ⚠ Each refusal blocks only the call it belongs to. A half-typed auto-refill pair must not
  // hold back a cap the customer did change, and vice versa.
  const saveRefused = (ruleChanged && !!reloadRefusal) || (capChanged && !!capRefusal)

  // ⚠⚠ THE CAP IS WRITTEN FIRST, AND THE ORDER IS NOT COSMETIC. Save creates the rule now
  // (app#197), so the old order left a window where the rule was live and its bound was not
  // written yet. The bound goes down first, then the rule it bounds. This is the order the switch
  // used when the switch was the armer.
  //
  // ⚠ `armIntent` is spent by a stored rule, and only by a stored rule. A Save that wrote the cap
  // alone must leave the flag set: the customer is still typing the pair, and clearing it there
  // would snap the switch Off under their hands. `run` throws before the clear if the call
  // failed, which is correct — a save that did not land keeps the switch On and the caption
  // still asking for what is missing.
  // ⚠⚠ BOTH WRITES GO OUT BEFORE EITHER RE-READ (app#293). The cap write starts the router's ~2 s
  // settle, and that margin exists for exactly the Save this function makes — an Owner raising the
  // maximum AND the amount together. Everything awaited between the two writes is spent out of it,
  // and `setSpendCap`'s own re-reads used to sit right there: at a ~700 ms RTT the rule committed
  // after the router had already decided, so it bought the OLD amount and the new one never fired
  // that period. `saveRefillSettings` states the order and is what a test can hold.
  const onSave = () =>
    run('reload', async () => {
      if (saveRefused) return
      // ⚠ Asked BEFORE the writes, for the reason `setSpendCap` asked it before its own: after
      // the call the stored figure IS the new one, so "did the maximum go up?" can no longer be
      // answered.
      const refillPermitted =
        (capChanged && capPermitsRefill(capCents, toCents(cap))) ||
        (ruleChanged && rulePermitsRefill(toCents(trigger), toCents(reload)))
      await saveRefillSettings({
        writeCap: capChanged ? () => writeSpendCap(toCents(cap)) : undefined,
        writeRule: ruleChanged ? () => writeAutoReload(toCents(trigger), toCents(reload)) : undefined,
        reread: rereadRefillState,
      })
      if (ruleChanged) {
        setArmIntent(false)
        // ⚠ A stored rule spends the cleared pair: there is nothing left to offer back, and the
        // acknowledgement would describe a write two presses ago (app#205).
        setCleared(undefined)
      }
      // ⚠ The LATE re-read the router's asynchronous purchase needs (nobgp-router#1328) is still
      // owned by the store actions for their own callers; this path asks for it once, after both
      // writes, for whichever of the two permits a refill.
      if (refillPermitted) rereadAfterRefillWrite()
    })

  // ⚠⚠ THE PRESS ALWAYS MOVES THE SWITCH (app#197). On enables the three fields and stores
  // nothing. Off clears the stored rule and clears the two numbers. Benoit, 2026-09-03: the
  // switch "does just enable the fields (and slides)", and "clicking it off should clear both
  // fields (below and amount)".
  //
  // ⚠⚠ NO PRESS IS REFUSED ANY MORE, WHICH IS THE WHOLE POINT. app#192 removed the disabled
  // switch, app#193 removed the pair rung, app#195 removed the cap rung. Each one moved the
  // refusal down a rung and left a control that answers a press with nothing. The rung is gone,
  // not moved: the switch is a disclosure control, and Save is what commits.
  //
  // ⚠⚠ OFF IS IMMEDIATE AND ON IS NOT, AND THE ASYMMETRY IS DELIBERATE. A stop to unattended
  // spending must take effect at once. A start to it must be an explicit Save.
  //
  // ⚠⚠ THE MONTHLY CAP KEEPS ITS VALUE ON OFF. It goes grey with the other two fields, and the
  // stored cap is not touched (Benoit, 2026-09-03: "it will be disabled (grey) if auto-refill is
  // disabled but will retain its value"). The cap is the organization's standing ceiling. A press
  // about the refill rule must not drop it out of view.
  //
  // ⚠ Off calls the router only when a rule is stored. A switch held On by `armIntent` alone has
  // nothing saved to remove, so that press is local and costs no round trip.
  const onToggleRefill = (on: boolean): void => {
    if (!on) {
      setArmIntent(false)
      setTrigger('')
      setReload('')
      // ⚠⚠ THE WRITE IS ACKNOWLEDGED, AND IT USED TO BE SILENT (app#205). The stored pair is read
      // BEFORE the call, because the reply overwrites the position with nulls, and it is recorded
      // only AFTER the call returns — `run` throws first if the write failed, and a rule that is
      // still stored must not be described as cleared.
      if (refillOn) {
        const prior = { trigger: position.autoReloadTriggerCents!, reload: position.autoReloadReloadCents! }
        void run('reload', async () => {
          await setAutoReload(null, null)
          setCleared(prior)
        })
      }
      return
    }
    // ⚠ The cursor goes to the first field that blocks the save, which `armRefusal` names. The
    // press is not refused; this only saves the customer the click. The focus itself is deferred
    // — the field is still disabled at this point.
    focusOnArm.current = arm ? { trigger: triggerRef, reload: reloadRef, cap: capRef }[arm.field].current : null
    setArmIntent(true)
  }

  // ⚠⚠ RESTORE FILLS THE FIELDS AND ARMS THE SWITCH. IT WRITES NOTHING (app#205). Off commits at
  // once and On does not (app#197), so handing the cleared pair back has to leave Save as the
  // committer — a restore that wrote would be the reversed gate app#197 removed, arriving from the
  // other side. The caption under the switch then says "not saved yet" about it, correctly.
  //
  // ⚠ The seeding effect does not undo this: a stored null does not clear the two fields, which is
  // the guard that protects a customer who is typing.
  const onRestore = (): void => {
    if (!cleared) return
    setTrigger(String(cleared.trigger / 100))
    setReload(String(cleared.reload / 100))
    setArmIntent(true)
  }

  // ⚠ ONE SPACING SCALE, and the larger-than-it-looks numbers are deliberate: MUI's outlined
  // InputLabel overhangs its box UPWARD, so a nominal 12px above a field renders as ~4px and the
  // label climbs into whatever sits above it. `formAlign.ts` carries the horizontal sibling.
  const SECTION = 3.25 // 26px — one section to the next
  const CONTROLS = 2.5 // 20px — a label or caption to the fields under it

  // ⚠⚠ A FIELD LABEL AND A SWITCH LABEL MUST READ THE SAME SIZE HERE, and by default they do NOT
  // — MEASURED in a harness, 12px/500 against 14px/400. `Buy credit` and `Auto-refill` are peers
  // on this panel: each one heads a control group, so a lighter, smaller field label inverts the
  // hierarchy and reads as an accident. Benoit asked about exactly this pair.
  //
  // ⚠ The 0.75 IS MUI's shrink transform, not a magic number: every outlined label is scaled by it
  // (`matrix(0.75,…)`), so a label SET at 14 RENDERS at 10.5. Dividing is what makes 14 come out
  // 14. Scaling rather than replacing the transform keeps the theme's tuned `top`/`left`, which
  // print the label ABOVE the box instead of in the notch (`MuiInputLabel` in `theme.tsx`).
  const LABEL_SHRINK = 0.75
  const fieldLabel = { fontSize: 14 / LABEL_SHRINK, fontWeight: 400 }

  const money = {
    slotProps: {
      input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
      inputLabel: { sx: fieldLabel },
    },
  } as const

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      {/* ⚠ A SECTION TITLE, ONE LEVEL UNDER THE PANEL (app#212). Both panels that hold this block
          — Billing and Usage — title themselves `h2`, so this is `h3` on either. */}
      <Typography variant="subtitle2" component="h3">Usage credit</Typography>

      {/* ⚠⚠ THE BALANCE IS THE FIGURE, and `$0.00` when there is none rather than absent: a
          customer with no credit is a normal state, and an empty space reads as a broken panel.
          Spent and the reset date drop to one meta line — still there, no longer competing with
          the number the page is about.

          ⚠⚠ `component="p"` AND IT IS NOT COSMETIC (app#212). `variant="h4"` alone emitted an
          `<h4>` — the balance announced as a heading TWO LEVELS ABOVE the panel containing it.
          The figure wants the size; it is a value, not a section. */}
      <Typography variant="h4" component="p" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, mt: 0.5 }}>
        {formatCents(position.balanceCents ?? 0)}
      </Typography>
      {/* ⚠⚠ THE RESET DATE BELONGS TO THE SPENT COUNT, NOT TO THE BALANCE (app#266). As a third
          item in a middot chain — "Balance · $0.00 spent · resets October 1" — it read as the
          balance resetting, two lines above a row that correctly says the purchased credit does
          not expire. The dash binds it to the count it follows, and the count names its month. */}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        Balance · {formatCents(position.spentThisPeriodCents)} spent in {periodName}
        {resetDue && ` — ${resetDue[0].toLowerCase()}${resetDue.slice(1)}`}
      </Typography>

      {/* ⚠⚠ ONE LINE PER KIND, NOT PER GRANT (Benoit, 2026-09-03: "multiple lines showing the
          amount, no date, same 'does not expire'"). Four top-ups printed four rows of
          "You bought $20.00 does not expire" — a transaction history with no dates, and the same
          three words down the block. `creditByKind` sums them, so the block answers the one
          question the balance raises: how much of this expires, and when. Typically two lines,
          never more than the kinds the router has.

          ⚠⚠ THE ORDER IS `consume_usage`'s SPEND ORDER, inherited rather than re-sorted, which is
          what makes the caption below true — see `creditByKind`. Do not sort these by size.

          ⚠ THE AMOUNTS KEEP THEIR OWN COLUMN and `tabular-nums`: the parts behave differently and
          a middot chain hides exactly that, while a flowed line starts each figure at a different
          x so two sums cannot be compared by eye. */}
      {byKind.length > 0 && (
        <Box
          sx={{
            mt: 1.25,
            display: 'grid',
            gridTemplateColumns: 'auto auto 1fr',
            columnGap: 1.25,
            rowGap: 0.4,
            alignItems: 'baseline',
          }}>
          {byKind.map(g => (
            <Box key={g.kind} sx={{ display: 'contents' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {g.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                {formatCents(g.remaining)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {grantExpiryLabel(g.expiresAt)}
              </Typography>
            </Box>
          ))}
          {/* ⚠⚠ THE CAPTION NEEDS SOMETHING THAT EXPIRES, not merely two lines. It explained the
              ORDER of the rows, so under four undated purchases it was answering a question the
              block did not raise — which is half of what made the old breakdown read as noise. */}
          {byKind.length > 1 && byKind.some(g => g.expiresAt) && (
            <Typography variant="caption" sx={{ gridColumn: '1 / -1', color: 'text.secondary', mt: 0.25 }}>
              Credit that expires is spent first.
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: SECTION }} onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      {/* ⚠⚠ EVERY CONTROL IS INSIDE THIS ONE CONDITIONAL (app#265). The read-only view keeps the
          balance above and the purchase list below, and nothing between them that a customer
          could press: the spending switch, Buy, Auto-refill and its three fields, and the refill
          gauge those fields explain. The in-flight purchase note stays outside it — money in
          flight is a fact about the balance, not a control. */}
      {!readOnly && (
      <>
      {/* ── Spend the balance ─────────────────────────────────────────────────
          ⚠⚠ IT IS NOT A PAUSE AND THE COPY MUST NOT READ AS ONE. Off keeps the balance and spends
          none of it: at the allowance the traffic is slowed and the machines stop, exactly as an
          empty balance would. Nothing is refunded and nothing expires either way. ⚠ The long
          sentence renders ONLY when it is off — on, the label is the whole explanation. */}
      <Box sx={{ mt: SECTION, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Switch
          checked={position.creditEnabled}
          disabled={!canManage || busy === 'spending'}
          onChange={e => void run('spending', () => setCreditSpending(e.target.checked))}
        />
        <Typography variant="body2">Use credit for overage</Typography>
      </Box>
      {!position.creditEnabled && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.4, ml: 5.5 }}>
          The balance is kept and none of it is spent. At the allowance your transfers are slowed and
          machines noBGP runs for you are stopped — exactly as an empty balance would. Nothing is
          refunded or expires.
        </Typography>
      )}

      <Divider sx={{ my: SECTION }} />

      {/* ── Buy ───────────────────────────────────────────────────────────────
          ⚠⚠ NO CAP CONSTRAINT HERE, AND ITS ABSENCE IS THE POINT (nobgp-router#1195). A manual
          purchase is an Owner acting deliberately against a card on file, so the ceiling on
          UNATTENDED spending has nothing to say about it. This used to disable the button at the
          cap, which produced the corner the redesign removes: throttled AND unable to buy the
          credit that would fix it. ⚠ The field's own label is the section heading — a separate
          "Buy credit" line above it said the same word twice. */}
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'flex-start' }}>
        <TextField
          size="small"
          type="number"
          label="Buy credit"
          value={amount}
          disabled={!canManage || busy === 'buy'}
          onChange={e => setAmount(e.target.value)}
          error={amount !== '' && !!buyRefusal}
          helperText={amount !== '' ? buyRefusal : ' '}
          sx={{ maxWidth: 180 }}
          {...money}
        />
        {/* ⚠⚠ A SECOND LOOK ABOVE THE THRESHOLD, AND NOT ON EVERY PRESS (app#206). This is the one
            control in the app where one click moves money — off session, immediately, against a
            card the customer cannot see from here — and it had no confirmation and no ceiling at
            all. `MAX_PURCHASE_CENTS` refuses the mistyped digit; this asks about the plausible
            one. ⚠ `DialogConfirm` calls `onClick` on EVERY press and `onConfirm` only from the
            dialog, so a purchase under the threshold — which opens no dialog — has to buy from
            `onClick`. ⚠ The offset rides on the WRAPPER, which is the flex item now; a margin on
            the Button would be measured inside the span instead of against the field. */}
        <DialogConfirm
          confirm={buyNeedsConfirm}
          title={`Buy ${formatCents(amountCents ?? 0)} of credit?`}
          action={`Buy ${formatCents(amountCents ?? 0)}`}
          disabled={buyDisabled}
          dialogChildren={
            <Typography variant="body2">
              {formatCents(amountCents ?? 0)} is charged to {chargeTarget} now. The credit is added to the
              balance as soon as the charge succeeds, and it does not expire.
            </Typography>
          }
          onClick={() => {
            if (!buyNeedsConfirm) void onBuy()
          }}
          onConfirm={() => void onBuy()}
          sx={{ ...besideField, display: 'inline-flex' }}>
          <Button variant="contained" disabled={buyDisabled}>
            Buy
          </Button>
        </DialogConfirm>
      </Stack>
      {/* ⚠⚠ THE SAME VALUE THE DIALOG USES (app#241). This said "the card on file" unconditionally,
          which is false for the Link and bank-account methods `describePaymentMethod` exists to
          handle — and it said it on BOTH panels, so even `/billing`, where the dialog names the
          real method, contradicted itself two lines apart. */}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        Charged to {chargeTarget}, now.
      </Typography>

      <Divider sx={{ my: SECTION }} />

      {/* ── Auto-refill ───────────────────────────────────────────────────────
          ⚠ The switch owns all three fields below it and there is no divider inside the group:
          the cap bounds automatic refills and nothing else, so it belongs to this rule and not to
          a section of its own. ⚠⚠ ALL THREE FIELDS ARE DEAD WHILE THE SWITCH IS OFF (app#197) —
          see the `Stack` below, which carries why. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Switch
          checked={switchOn}
          disabled={!canManage || busy === 'reload'}
          onChange={e => onToggleRefill(e.target.checked)}
        />
        <Typography variant="body2">Auto-refill</Typography>
      </Box>
      {/* ⚠ ONE LINE, AND IT ALWAYS NAMES THE NEXT ACTION. `armRefusal` answers which field stops
          the rule being saved, so the caption says that rather than the generic "set both".

          ⚠⚠ ON MEANS TWO DIFFERENT THINGS AND THE DIFFERENCE IS MONEY (app#197). The switch moves
          on every press, so On is either a STORED rule that will charge a card later, or a rule
          the customer asked for that charges nothing at all. One sentence for both would be a
          wrong statement about the customer's money, which is the one thing this panel must not
          make. The unsaved On is warning-coloured, says "not saved yet" in those words, and names
          either the field that blocks it or the button that finishes it.

          ⚠⚠ AND THE STORED BRANCH CARRIES THE $0 CAP. A rule under a $0 cap is a rule
          `refillHeadroomCents` bounds to nothing, so the plain "Buys $20.00 whenever…" sentence
          would be wrong in the same way. It is reached two ways: the cap defaults to $0
          (router#933), and an owner whose rule is on can type 0 into the cap and press Save, which
          `capRefusal` allows because zero is a legal cap.

          ⚠⚠ A SPENT CAP IS THE SAME WRONG SENTENCE, AND IT WAS SILENT HERE. A cap above $0 that
          this period's refills have used up buys nothing more until the period resets, and the
          plain sentence promised a purchase in the present tense that the rule cannot make. ⚠⚠ It
          then said "the $10.00 cap is fully used" of a cap with up to $4.99 left, because the rung
          asks the FLOOR and not zero — `capExhaustedClause` names the remainder where there is one
          (app#303). The
          refill row below draws that state in warning colour, which is why it was left; app#195
          then made `BillingBanner` name it in WORDS — "its monthly cap has nothing left" — and
          two surfaces disagreeing about the customer's money is the defect, not the missing
          sentence. `autoReloadCanFire` is the one rule both now ask.

          ⚠ THE $0 BRANCH STAYS SEPARATE, AND IS ASKED FIRST. It prints the $0 figure, which is
          the whole of what that owner has to change; "the $0.00 cap is fully used" would be an
          odd thing to say about a cap that was never set. A $0 cap cannot fire either, so the
          order — not the tests — is what keeps the two apart.

          ⚠⚠ AND A CAP UNDER THE REFILL FLOOR GETS A THIRD SENTENCE, because the spent-cap one is
          FALSE about it. `autoReloadCanFire` asks the refill floor now, so a $3 cap with nothing
          refilled reaches `!refillCanFire` — and "the $3.00 cap is fully used" says money was
          spent when none was, and "nothing more is bought in ${periodName}" promises a refill
          next month that a sub-floor cap can never make. That cap is too small to be worth
          charging, not used up, so the sentence says that instead. It is app#204's row 2.

          ⚠⚠ AND IT ASKS `!refillCanFire` AS WELL AS THE CAP, WHICH IS NOT BELT AND BRACES. A
          rule saved under the floor before nobgp-router#1333 still buys its full amount — the
          floor bounds the TRUNCATION alone — so a $3.00 rule under a $4.00 cap fires, and the
          cap test on its own called it dead on the screen while the sweep bought for it. The
          rung belongs to a cap that is BOTH sub-floor and unable to buy; `refillFloorCents`
          carries the reason.

          ⚠⚠ AND A TRUNCATING CAP IS A FIFTH RUNG, BECAUSE THE THREE ABOVE IT SUPPRESS ONLY THE
          DEAD CAPS (app#222). A cap that is legal, unspent and merely SMALLER than what is left to
          ask for still fires — it just buys less. `refillCanFire` is true there, so the case fell
          through to the plain sentence, which promised the full amount. Measured on prod with $60
          refilled against a $70 cap: "Buys $20.00" beside $10.00 of headroom. The sentence leads
          with what actually happens and then says why, so the customer reads a true figure first.

          ⚠ IT IS ASKED LAST OF THE FIVE, and the order is again the message: a $0, sub-floor or
          spent cap buys NOTHING, and "buys $0.00" would be a worse sentence than the three that
          name a remedy. `storedRefillTruncation` answers null for all three, so the order and the
          function agree rather than either alone being load-bearing.

          ⚠⚠ AND A REFILL THAT DID NOT COMPLETE IS A RUNG OF ITS OWN, BETWEEN THE DEAD CAPS AND
          THE TRUNCATION (app#271). The rule fired and the purchase failed, and the caption still
          promised "Buys $5.00 whenever…" — the failure was on the panel only as a purchase row
          further down. It sits below the three cap rungs because a rule that cannot fire has its
          remedy named there, and a failure is then history; it sits above the truncation because
          a refill that failed is the larger fact than an amount that is short.

          ⚠ IT NAMES NO CARD. The row records no instrument, and only one of the router's three
          failure causes is the card (declined, needs an extra confirmation step, generic). The
          caption repeats the row's own `failure` sentence — the router's mapped text, the same
          text the purchase list prints — and that sentence names the remedy where there is one.
          `failure` is never null on a failed row (router contract); the fallback is for a row
          that arrives without it. ⚠ It does not say the rule has stopped: nobgp-router#1307
          adds a back-off, and today the rule retries.

          ⚠ THE OFF BRANCH NAMES NO FIELD, and it used to. The three fields are disabled and the
          two numbers are empty while the switch is Off, so pointing at one is an instruction the
          customer cannot act on. The next action there is the switch. */}
      <Typography
        variant="caption"
        sx={{
          // ⚠ THE SAME ORDER AS THE TEXT BELOW, AND NOT THE SAME TEST. `refillOn` is asked
          // first in both, so one state picks one colour and one sentence. The two branched in
          // opposite order until app#195's review, and a rule that turned On from another
          // surface was painted in the wrong one.
          //
          // ⚠ IT COLLAPSES THE TEXT'S THREE WARNING BRANCHES INTO ONE TEST, and it may: a $0
          // cap, a cap under the refill floor and a spent cap are all `!refillCanFire`, and they
          // differ in WORDS alone. Splitting the colour as well would be tests that can only
          // ever agree.
          color: refillOn
            ? refillCanFire && !refused
              ? 'text.secondary'
              : 'warning.main'
            : armIntent
              ? 'warning.main'
              : 'text.secondary',
          display: 'block',
          mt: 0.4,
          ml: 5.5,
        }}>
        {refillOn
          ? capCents === 0
            ? `On, but nothing is bought: the monthly cap is $0. Raise it to buy ${formatCents(position.autoReloadReloadCents!)} whenever the balance falls below ${formatCents(position.autoReloadTriggerCents!)}.`
            : capCents < MIN_AUTO_REFILL_CENTS && !refillCanFire
              ? `On, but nothing is bought: the ${formatCents(capCents)} monthly cap is below the ${formatCents(MIN_AUTO_REFILL_CENTS)} an automatic refill has to be worth. Raise it to buy ${formatCents(position.autoReloadReloadCents!)} whenever the balance falls below ${formatCents(position.autoReloadTriggerCents!)}.`
              : !refillCanFire
                ? `On, but nothing more is bought in ${periodName}: the ${capExhaustedClause(position)}. Raise the cap to buy ${formatCents(position.autoReloadReloadCents!)} whenever the balance falls below ${formatCents(position.autoReloadTriggerCents!)}.`
                : refused
                  ? `On, but the last automatic refill (${instantUTC(refused.createdAt)}) did not complete. ${refused.failure ?? 'No credit was added.'}`
                  : storedTruncatedTo !== null
                    ? `Buys ${formatCents(storedTruncatedTo)} whenever the balance falls below ${formatCents(position.autoReloadTriggerCents!)}: the ${formatCents(capCents)} cap has ${formatCents(storedTruncatedTo)} left in ${periodName}, not the ${formatCents(position.autoReloadReloadCents!)} the rule asks for.`
                    : `Buys ${formatCents(position.autoReloadReloadCents!)} whenever the balance falls below ${formatCents(position.autoReloadTriggerCents!)}.`
          : armIntent
            ? arm
              ? `On, but not saved yet: ${arm.message.charAt(0).toLowerCase()}${arm.message.slice(1)} Nothing is charged until you press Save.`
              : 'On, but not saved yet: press Save to store the rule. Nothing is charged until you press Save.'
            : 'Off. Nothing is charged to the card unless you buy credit yourself. Turn it on to set a rule.'}
      </Typography>

      {/* ⚠⚠ THE OFF PRESS WROTE AND SAID NOTHING, AND THAT SILENCE IS app#205. It clears the
          stored rule at once — the right behaviour, app#197's decided design — while Save stays
          disabled, so the panel that had just taught the customer "nothing is charged until you
          press Save" gave no sign that anything had been stored. This says what happened, and
          offers the pair back rather than leaving it to be retyped from memory.

          ⚠ It renders only while the switch is OFF: once it is on, the caption above is talking
          about the rule being typed and a sentence about the cleared one would be answering a
          question nobody is asking any more. */}
      {cleared && !switchOn && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', mt: 0.4, ml: 5.5 }}>
          <Typography variant="caption">
            {/* ⚠ "It was set to buy", never "It bought": a rule can be cleared without ever having
                fired, and saying it bought money the customer was never charged is a wrong
                statement about their money. */}
            Auto-refill is off and the stored rule is cleared. It was set to buy{' '}
            {formatCents(cleared.reload)} whenever the balance fell below {formatCents(cleared.trigger)}.
          </Typography>
          <Button
            size="small"
            variant="text"
            disabled={!canManage || busy === 'reload'}
            onClick={onRestore}>
            Restore
          </Button>
        </Box>
      )}

      {/* ⚠ `useFlexGap`: without it Stack's own `margin: 0` on every child outranks the button's
          `sx`, and the offset is dead CSS. `formAlign.ts` carries the measurement. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        useFlexGap
        // ⚠⚠ DIMMED WHILE THE SWITCH IS OFF, AND THIS IS NOT THE DIMMING app#193 REMOVED
        // (app#197). That one sat beside a switch that was ITSELF dead until the fields held a
        // valid rule, so the whole row was unusable and nothing said why. The switch always moves
        // now, and moving it is what wakes these three fields. Dead fields beside a live switch
        // read as one control with one obvious next press.
        //
        // ⚠⚠ AND THE DIMMING IS A GUARD, not a hint. `ruleChanged` lets Save create a rule now,
        // so an Owner who could type into these boxes while the switch read Off could enrol a
        // card in unattended charges without ever touching the switch. There is nothing to type.
        sx={{ alignItems: 'flex-start', mt: CONTROLS }}>
        <TextField
          size="small"
          type="number"
          label="When below"
          value={trigger}
          inputRef={triggerRef}
          disabled={!canManage || busy === 'reload' || !switchOn}
          onChange={e => setTrigger(e.target.value)}
          error={!!triggerError}
          helperText={triggerError ?? ' '}
          sx={{ maxWidth: 150 }}
          {...money}
        />
        <TextField
          size="small"
          type="number"
          label="Buy"
          value={reload}
          inputRef={reloadRef}
          disabled={!canManage || busy === 'reload' || !switchOn}
          onChange={e => setReload(e.target.value)}
          error={!!reloadError}
          helperText={reloadError ?? ' '}
          sx={{ maxWidth: 130 }}
          {...money}
        />
        {/* ⚠ NO "Unlimited" and no empty-means-no-cap: `set_spend_cap` refuses the idea, and an
            absent cap IS the $0 default (router#933). ⚠ An EMPTY field leaves the stored cap
            alone — type 0 to mean zero; see `capChanged`. */}
        <TextField
          size="small"
          type="number"
          label="Monthly cap"
          value={cap}
          inputRef={capRef}
          disabled={!canManage || busy === 'reload' || !switchOn}
          onChange={e => setCap(e.target.value)}
          error={cap !== '' && !!capRefusal}
          helperText={(cap !== '' && capRefusal) || ' '}
          sx={{ maxWidth: 150 }}
          {...money}
        />
        <Button
          disabled={!canManage || busy === 'reload' || saveRefused || (!ruleChanged && !capChanged)}
          onClick={() => void onSave()}
          sx={besideFieldRow}>
          Save
        </Button>
      </Stack>

      {/* ⚠⚠ THE BAR COUNTS REFILLS, NOT OVERAGE — the same units as the field above it. Measured
          against `autoRefilledThisPeriodCents`, which is what the cap actually bounds since
          #1195; drawing it from `purchasedThisPeriodCents` would count a manual purchase against
          a cap that no longer bounds one. */}
      {/* ⚠⚠ AND IT IS HIDDEN WHEN THE ROUTER DID NOT SEND THE FIGURE, rather than drawn at zero.
          `autoRefilledThisPeriodCents` is optional for the deploy window only (router first, this
          second), and an absent figure is NOT a refilled total of nothing: an org that had already
          refilled $60 would be told it had refilled $0 of its $100, which is a wrong statement
          about the customer's money made in the one window where nobody is watching for it.

          ⚠⚠ A $0 CAP NO LONGER TAKES THE GAUGE OFF THE SCREEN (app#201), and that condition is the
          only one removed: a zeroed gauge is information and an absent one is not, and $0 is the
          moment the customer most needs to see why nothing is bought. `refillGaugePercent` answers
          the division the cap of zero would otherwise make NaN.

          ⚠⚠ AND THE LABEL NO LONGER SAYS "Refilled". The numerator is `auto_refilled_cents()`,
          which sums `status in ('pending','succeeded')` — so a reservation that was never charged
          and never granted was reported to the customer as money refilled (app#201). "Automatic
          refills" is true of a purchase in flight; "refilled" was not, and the line below names
          the part that has not landed.

          ⚠ THE COLOUR ASKS THE REFILL FLOOR, NOT ZERO. A remainder under the floor is not worth
          charging (`refillAmount`, router#1195), so a $20 rule with $4.99 of cap left is as spent
          as one with nothing left. ⚠ It asks `refillFloorCents` and not the constant, because a
          rule saved under the floor still buys its full amount and must not be painted as dead.
          An unsettled purchase paints it too: green reads as healthy and money in flight is not.

          ⚠ AND IT ASKS THE SWITCH BEFORE IT WARNS ABOUT THE CAP, which it did not have to while a
          $0 cap hid the row: an organization with no rule at all has a $0 budget of which $0 is
          used, and there is nothing wrong with that. A warning colour on every default account is
          an alarm about a control its owner has not touched. An unsettled purchase is different —
          that is the customer's money, rule or no rule — so it paints either way.

          ⚠⚠ AND THE ROW IS GONE WHEN THE SWITCH IS OFF AND NO CAP IS SET (app#278). The un-hiding
          above kept the line and lost the guard: with no rule and no cap it printed
          "$5.00 / $0.00" after an earlier refill, and "$0.00 / $0.00" on every new subscriber's
          first screen — a zero denominator under real money, read as over cap. `showsRefillGauge`
          holds the rule and keeps app#201's property: a switch that is On under a $0 cap still
          shows the bar and its warning. It takes `armIntent` so that the row appears the moment
          the switch is pressed, as the colour already does. */}
      {position.autoRefilledThisPeriodCents !== undefined && showsRefillGauge(position, armIntent) && (
        <Box sx={{ maxWidth: 360 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            Automatic refills in {periodName} {formatCents(refilled)} / {formatCents(capCents)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={refillGaugePercent(position)}
            color={unsettled > 0 || (switchOn && refillHeadroom < refillFloorCents(position)) ? 'warning' : 'success'}
            sx={{ mt: 0.75 }}
          />
        </Box>
      )}
      </>
      )}

      {/* ⚠⚠ MONEY THE PANEL COUNTS AS BOUGHT AND THE BALANCE DOES NOT HOLD (app#201). Measured on
          prod: money in $20, money out $0, balance $0 — three figures that cannot all be true,
          with nothing on the panel noticing. A reservation is the customer's money in flight and
          the direction is MORE visibility, not less, so it is named here rather than folded away.

          ⚠ IT IS A FIELD NOW (router#1206). `pending_purchase_cents` is a READ of the pending rows
          rather than arithmetic over three totals, and `oldest_pending_purchase_at` carries the
          AGE this sentence could not previously state — which is the half that matters, because
          minutes are ordinary and hours mean the purchase needs a person. `pendingPurchaseCents`
          keeps the old residual as a fallback for the deploy window only, and says when to remove
          it. ⚠ The COUNT of rows is still not on the payload and is still not shown.

          ⚠ THE PAUSE IS THE SENTENCE THAT MATTERS. `RunAutoReload` skips an org that has ANY
          pending purchase — every kind, so one manual Buy holds the automatic sweep off for the
          rest of the month — and an owner had no way to learn that from this screen. */}
      {unsettled > 0 && (
        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.75, maxWidth: 420 }}>
          {formatCents(unsettled)} bought in {periodName} is not in the balance yet
          {unsettledFor ? ` — in flight ${unsettledFor.text}` : ''}. Credit is added when the charge settles, and
          automatic refills stay paused until it does.
          {unsettledFor?.stale ? ' A purchase this old usually needs support to settle it.' : ''}
        </Typography>
      )}

      {/* ── Purchases ─────────────────────────────────────────────────────────
          ⚠⚠ THE ONLY IN-PRODUCT RECORD OF A CARD CHARGE (nobgp-router#1209). A credit purchase is
          a bare PaymentIntent and deliberately NOT an invoice — an invoice implies terms, a due
          date and dunning, none of which apply to a charge that already settled — so it appears on
          no invoice list. Before this it appeared nowhere: money left a card, the balance rose,
          and nothing in the product said when or how much. After the Stripe switch that is a real
          statement line the customer cannot reconcile against anything we ever showed them.

          ⚠ NULL IS NOT EMPTY. `null` means the list could not be read — an older router with no
          `op=purchases`, or a failed call — and renders nothing at all rather than an empty state,
          because "you bought nothing this month" is a claim and this does not have the standing to
          make it. `[]` renders the empty state and does.

          ⚠ A FAILED row still belongs here, and says NOT CHARGED in as many words. The customer's
          only question about a failure is whether their money moved. The reason beside it is the
          router's MAPPED sentence, never the stored text — that column holds engineering prose
          and, for a Stripe error, a link into our own dashboard. */}
      {creditPurchases !== null && (
        <>
          <Divider sx={{ my: SECTION }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
            Purchases in {periodName}
          </Typography>
          {creditPurchases.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              No credit bought in {periodName}.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', columnGap: 1.5, rowGap: 0.4 }}>
              {creditPurchases.slice(0, MAX_PURCHASE_ROWS).map(p => (
                <Fragment key={p.id}>
                  {/* ⚠ AN INSTANT, NOT A BOUNDARY (app#256). `periodDateUTC` is day-granular and
                      right for the renewal line; here it rendered three identical rows on a list
                      whose only job is telling one charge from another. */}
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {instantUTC(p.createdAt)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatCents(p.amountCents)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: p.status === 'failed' ? 'warning.main' : 'text.secondary' }}>
                    {purchaseStatusLabel(p.status)}
                    {p.kind === 'auto_reload' ? ' · automatic' : ''}
                    {p.failure ? ` — ${p.failure}` : ''}
                  </Typography>
                </Fragment>
              ))}
            </Box>
          )}
          {/* ⚠⚠ THE LONGEST LIST BELONGS TO THE CUSTOMER IN THE MOST TROUBLE. The router caps at
              500 rows (`db.MaxCreditPurchaseRows`) and names the case that reaches it: auto-refill
              on against a card that keeps being declined writes one FAILED row per 15-minute
              sweep. Drawing them all would put 500 rows on the billing page for exactly the person
              who came to it to find out why their card is not working. The count is STATED, so a
              short list never reads as the whole month. */}
          {creditPurchases.length > MAX_PURCHASE_ROWS && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.4 }}>
              {creditPurchases.length - MAX_PURCHASE_ROWS} older{' '}
              {creditPurchases.length - MAX_PURCHASE_ROWS === 1 ? 'purchase is' : 'purchases are'} not shown.
            </Typography>
          )}
        </>
      )}

      {/* ⚠⚠ THE WARNING READS THE DRAFT; THE CAPTION UNDER THE SWITCH READS WHAT IS STORED
          (app#204). Four caps measured on prod — $0, $0.25, $5 under a $20 rule, and $15 with $20
          already refilled — all left Save enabled and said nothing, so the customer learned their
          rule was dead by storing it first. `refillOutlook` carries the four sentences and why the
          $5 one does not say "nothing".

          ⚠⚠ IT WARNS AND SAVE STAYS ENABLED, deliberately. Parking a rule under a $0 cap is a real
          need, because the off switch destroys the pair (app#205) — and refusing on a $0 cap is
          exactly what Benoit reversed on the switch on 2026-09-03.

          ⚠ NOT WHILE THE SWITCH IS OFF (app#197): the cap field is disabled there, so "Raise it"
          would name a control the customer cannot reach. ⚠ And not while the three fields still
          hold the stored rule unedited — the caption above is then saying it about the live rule,
          and the same sentence twice is one sentence nobody reads. */}
      {switchOn && draftOutlook && (!refillOn || capChanged || ruleChanged) && (
        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.75, maxWidth: 420 }}>
          {draftOutlook.message}
        </Typography>
      )}
    </Paper>
  )
}
